import { useState } from 'react';
import { ChevronRight, RotateCcw, XCircle } from 'lucide-react';
import { Button, Textarea } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  JOURNEY_STEP_ORDER,
  useAdvanceJourneyStep,
  useMarkJourneyLost,
  useReengageJourney,
  type CustomerJourney,
  type JourneyStep,
} from '@/api/customer-journey';
import { stepLabel, stepIndex, isLostStep, isWonStep } from './sale-process-helpers';
import { errorMessage } from './sale-process-error';

/**
 * Lifecycle actions for the active journey: Advance to the next pipeline step,
 * Mark lost (reason required), or Re-engage a lost journey. Locked/won journeys
 * expose no mutating actions. Success/error surface via toast; each action is
 * wired but only fires on explicit user confirmation.
 */

type ActionKind = 'advance' | 'lost' | 'reengage' | null;

export function JourneyActions({
  customerId,
  journey,
}: {
  customerId: number;
  journey: CustomerJourney;
}) {
  const toast = useToast();
  const [open, setOpen] = useState<ActionKind>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  const advance = useAdvanceJourneyStep(customerId, journey.customer_journey_id);
  const markLost = useMarkJourneyLost(customerId, journey.customer_journey_id);
  const reengage = useReengageJourney(customerId, journey.customer_journey_id);

  const step = journey.customer_journey_step;
  const lost = isLostStep(step);
  const won = isWonStep(step);
  const locked = !!journey.customer_journey_locked;

  const idx = stepIndex(step);
  const nextStep: JourneyStep | undefined =
    idx >= 0 && idx < JOURNEY_STEP_ORDER.length - 1 ? JOURNEY_STEP_ORDER[idx + 1] : undefined;

  const close = () => {
    setOpen(null);
    setReason('');
    setReasonError(false);
  };

  const onError = (label: string) => (err: unknown) =>
    toast.error(`Failed to ${label}`, { description: errorMessage(err) });
  const onDone = (label: string) => () => {
    toast.success(label);
    close();
  };

  const runAdvance = () => {
    if (!nextStep) return;
    advance.mutate(
      { customer_journey_step: nextStep },
      { onSuccess: onDone(`Moved to ${stepLabel(nextStep)}`), onError: onError('advance step') },
    );
  };

  const runMarkLost = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(true);
      return;
    }
    markLost.mutate(
      { reason: trimmed },
      { onSuccess: onDone('Journey marked lost'), onError: onError('mark lost') },
    );
  };

  const runReengage = () => {
    reengage.mutate(
      { note: reason.trim() },
      { onSuccess: onDone('Journey re-engaged'), onError: onError('re-engage') },
    );
  };

  // Nothing actionable on a won/locked-and-not-lost journey.
  if (won || (locked && !lost)) return null;

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        {!lost && nextStep ? (
          <Button size="sm" variant="primary" onClick={() => setOpen('advance')}>
            <ChevronRight size={14} style={{ marginRight: 'var(--vn-space-1)' }} />
            Advance to {stepLabel(nextStep)}
          </Button>
        ) : null}
        {!lost ? (
          <Button size="sm" variant="secondary" onClick={() => setOpen('lost')}>
            <XCircle size={14} style={{ marginRight: 'var(--vn-space-1)' }} />
            Mark lost
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setOpen('reengage')}>
            <RotateCcw size={14} style={{ marginRight: 'var(--vn-space-1)' }} />
            Re-engage
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={open === 'advance'}
        onOpenChange={(o) => (o ? setOpen('advance') : close())}
        title="Advance journey"
        description={nextStep ? `Move this journey to "${stepLabel(nextStep)}".` : undefined}
        confirmLabel="Advance"
        loading={advance.isPending}
        onConfirm={runAdvance}
      />

      <ConfirmDialog
        open={open === 'lost'}
        onOpenChange={(o) => (o ? setOpen('lost') : close())}
        title="Mark journey lost"
        description="Record why this deal was lost. The journey is closed but can be re-engaged later."
        confirmLabel="Mark lost"
        destructive
        loading={markLost.isPending}
        onConfirm={runMarkLost}
      >
        <Field label="Lost reason" required error={reasonError ? 'A reason is required.' : undefined}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(false);
              }}
              placeholder="Why was this deal lost?"
            />
          )}
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={open === 'reengage'}
        onOpenChange={(o) => (o ? setOpen('reengage') : close())}
        title="Re-engage journey"
        description="Re-open this lost journey and resume the pipeline. Add a note about why it's being re-engaged."
        confirmLabel="Re-engage"
        loading={reengage.isPending}
        onConfirm={runReengage}
      >
        <Field label="Note">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this journey being re-engaged?"
            />
          )}
        </Field>
      </ConfirmDialog>
    </>
  );
}

export default JourneyActions;
